import { validEINPrefixes } from 'types/einPrefixes'
import memo from 'lodash/memoize'

////////////////////////////////////////////////////////////////
// 🚨WARNING - 🚨WARNING - 🚨WARNING - 🚨WARNING - 🚨WARNING //
////////////////////////////////////////////////////////////////
// All these methods must be memoized or will result in infinite loops
// if called within a react render/hook
////////////////////////////////////////////////////////////////
// TODO add tests to catch any cases where this is not true
////////////////////////////////////////////////////////////////

const withMessage = memo((fn) => memo((msg) => makeValidation((v) => fn(v), msg)))
const withRequired = memo((fn) => memo(() => groupValidations([required, fn])))
const and = memo((fn) => memo((other) => groupValidations([fn, other])))

export function makeValidation(fn, defaultMsg) {
  const func = (...args) => fn(...args) || PASSED
  func.withMessage = withMessage(func)
  func.required = withRequired(func)
  func.and = and(func)
  func.message = defaultMsg
  return func
}

export const FAILED = Symbol('FAILED')
export const PASSED = Symbol('PASSED')

export const runValidations = (validations) => (value) => {
  validations = Array.isArray(validations) ? validations : [validations]
  const failed = validations.find((v) => v(value) === FAILED)
  if (failed) {
    return failed.message
  }
}

export const groupValidations = (validations, message) => {
  const resetMessage = !message
  const fn = (value) => {
    const firstFail = validations.find((v) => v(value) === FAILED)
    if (firstFail) {
      if (resetMessage) {
        message = firstFail.message
      }
      return FAILED
    }
  }
  fn.withMessage = withMessage(fn)
  fn.required = withRequired(fn)
  fn.and = and(fn)
  Object.defineProperty(fn, 'message', {
    get() {
      return message || 'Not Valid'
    },
  })
  return fn
}

export const invalid = makeValidation(() => FAILED, 'Invalid')

export const required = makeValidation((value) => {
  if (!value) {
    return FAILED
  }
}, 'Required')

export const failWith = memo((message) =>
  makeValidation(() => {
    return FAILED
  }, message)
)

export const email = makeValidation((value) => {
  const re = new RegExp(/^[A-Z0-9._%+-]+@[A-Z0-9-.]+\.[A-Z]*[^\con]$/i)
  if (value && !value.match(re)) {
    return FAILED
  }
}, 'Must be a valid email')

export const phone = makeValidation((value) => {
  if (value && !value.match(/^[2-9]\d{9}$/)) {
    return FAILED
  }
}, 'Must be a valid phone number')

export const ssn = makeValidation((value) => {
  if (!value) {
    return false
  }
  if (!value.match(/^\d{9}$/)) {
    return FAILED // Must be exactly 9 digits
  }
  if (value.match(/^9/)) {
    return FAILED // Cannot start with 9
  }
  if (value.match(/^666/)) {
    return FAILED // Cannot start with 666
  }
  if (value.match(/^000/)) {
    return FAILED // Cannot start with 000
  }
  if (value.match(/^\d{3}00/)) {
    return FAILED // Cannot have 00 for middle section
  }
  if (value.match(/0000$/)) {
    return FAILED // Cannot end with 0000
  }
}, 'Please enter a valid Social Security number')

export const ein = makeValidation((value) => {
  if (!value) {
    return false
  }
  const prefix = value.substring(0, 2)
  if (!validEINPrefixes.includes(prefix)) {
    return FAILED // Invalid prefix
  }
}, 'This is not a valid EIN. Do not enter your SSN.')

export const photo = makeValidation((value) => {
  if (value && !value.dataUrl) {
    return FAILED
  }
}, 'Photo Required')

export const integer = makeValidation((value) => {
  if (value && !value.match(/^\d+$/)) {
    return FAILED
  }
}, 'Must be an integer')

export const maxLength = memo((count) =>
  makeValidation((value) => {
    if (value && value.length > count) {
      return FAILED
    }
  }, `Maximum ${count} characters required`)
)

export const minLength = memo((count) =>
  makeValidation((value) => {
    if (value && value.length < count) {
      return FAILED
    }
  }, `Minimum ${count} characters required`)
)

export const exactLength = memo((length) =>
  groupValidations([minLength(length), maxLength(length)], `Exactly ${length} characters required`)
)

export const betweenLength = memo(
  (a, b) => groupValidations([minLength(a), maxLength(b)], `Must be between ${a} and ${b} characters`),
  (min, max) => {
    // By default memo only memoizes the first arg.
    // This is to make sure it is memo'd on both.
    return `${min}:${max}`
  }
)

export const doesNotContainDob = makeValidation((value) => {
  if (value && value.match(/\d{1,2} ?\/ ?\d{1,2} ?\/ ?\d{2}/)) {
    return FAILED
  }
}, 'Please do not enter any personal information, such as birthdays or names, into this field')

export const not = memo((other) =>
  makeValidation((value) => {
    if (other === value) {
      return FAILED
    }
  }, `Cannot Be ${other}`)
)

export const exactly = memo((other) =>
  makeValidation((value) => {
    if (other !== value) {
      return FAILED
    }
  }, `Must be exactly "${other}"`)
)

export const oneOf = memo((items) =>
  makeValidation(
    (value) => {
      if (!items.includes(value)) {
        return FAILED
      }
    },
    `Must be one of "${items.join(', ')}"`
  )
)

export const lte = memo((number) =>
  makeValidation((value) => {
    if (value && Number(value.replace(/[^\d.]/g, '')) > number) {
      return FAILED
    }
  }, `Cannot exceed ${number}`)
)

export const gte = memo((number) =>
  makeValidation((value) => {
    if (value && Number(value.replace(/[^\d.]/g, '')) < number) {
      return FAILED
    }
  }, `Must be at least ${number}`)
)

export const decimal = makeValidation((value) => {
  if (value && value.split('.').length > 2) {
    return FAILED
  }
}, 'Only 1 decimal place allowed')

export const date = makeValidation((value) => {
  if (value && !value.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    return FAILED
  }
}, 'Must be MM DD YYYY')

export const monthYear = makeValidation((value) => {
  if (value && !value.match(/^\d{2}\/\d{4}$/)) {
    return FAILED
  }
}, 'Must be MM YYYY')

export const validMonthYear = makeValidation((value) => {
  if (isMonthYear(value)) {
    const [month] = value.split('/')
    if (Number(month) < 1 || Number(month) > 12) {
      return FAILED
    }
  }
}, 'Date is invalid')

function isMonthYear(value) {
  return value && value.match(/^\d{2}\/\d{4}$/)
}

export const validDate = makeValidation((value) => {
  if (isMonthDayYear(value)) {
    const date = new Date(value)
    if (isNaN(date.getTime())) {
      return FAILED
    }
  }
}, 'Date is invalid')

function isMonthDayYear(value) {
  return value && value.match(/^\d{2}\/\d{2}\/\d{4}$/)
}

export const after = memo((date) =>
  makeValidation(
    (value) => {
      if (!value) {
        return
      }
      if (value.match(/^\d{2}\/\d{4}$/)) {
        const [month, year] = value.split('/')
        value = `${month}/01/${year}`
      }
      if (createDate(value) <= new Date(date)) {
        return FAILED
      }
    },
    `Must be after ${new Date(date).toLocaleDateString('en', { month: '2-digit', day: '2-digit', year: 'numeric' })}`
  )
)

export const legalAdult = makeValidation((value) => {
  const yearsAgo18 = new Date().setFullYear(new Date().getFullYear() - 18)
  const comparing = createDate(value).getTime()
  if (comparing > yearsAgo18) {
    return FAILED
  }
}, `Must be 18 years or older`)

export const stillLiving = makeValidation((value) => {
  const yearsAgo100 = new Date().setFullYear(new Date().getFullYear() - 125)
  const comparing = createDate(value).getTime()
  if (comparing < yearsAgo100) {
    return FAILED
  }
}, `Must be alive`)

function createDate(value) {
  if (!value) {
    return new Date()
  }

  const date = new Date(value)
  if (isMonthYear(value)) {
    const [, year] = value.split('/')
    date.setFullYear(year)
  } else if (isMonthDayYear(value)) {
    const [, , year] = value.split('/')
    date.setFullYear(year)
  }
  return date
}

export const decimalSpaces = makeValidation((value) => {
  if (value && !value.match(/^\d*\.?\d{0,2}$/)) return FAILED
}, 'Cannot be more than 2 decimals spaces')

export const hasNumbers = makeValidation((value) => {
  if (value && !value.match(/(?=.*\d)/)) return FAILED
}, `Must contain at least 1 number`)

export const hasLowercase = makeValidation((value) => {
  if (value && !value.match(/(?=.*[a-z])/)) return FAILED
}, `Must contain at least 1 lowercase`)

export const hasUppercase = makeValidation((value) => {
  if (value && !value.match(/(?=.*[A-Z])/)) return FAILED
}, `Must contain at least 1 uppercase`)

export const hasSpecial = makeValidation((value) => {
  if (value && !value.match(/(?=.*[-+_!@#$%^&*., ?])/)) return FAILED
}, `Must contain at least 1 special`)
